import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { getGridStringOperators, GridColDef, GridRenderCellParams, GridValueGetter } from '@mui/x-data-grid-premium'
import RenderServiceIcon from 'components/RenderServiceIcons/RenderServiceIcon'
import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderStatusCell from 'components/Deals/grid/cells/RenderStatusCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import { customMonthRangeFilterOperators } from 'components/DataGrid/operators/customMonthRangeFilter'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { parseNumeric } from 'components/DataGrid/operators/formatNumbers'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { OrganizationTableItem } from 'types/organizations_table'
import { formatDateString } from 'utils/formatDayjsDateTime'

const currencyValueFormatter = ({ value }: { value: number }) =>
  value == null
    ? ''
    : (value as number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export const renderProductsColumns = (
  density: 'compact' | 'standard' | 'comfortable',
  openInfo: (product: OrganizationTableItem) => void,
  organizations: OrganizationTableItem[],
  deals: OrganizationTableItem[]
) => {
  const densityStyles = getDensityStyles(density)

  const columns: GridColDef[] = [
    // {
    //   field: 'productName',
    //   headerName: 'Product Name',
    //   width: 100,
    //   headerAlign: 'left',
    //   aggregable: false,
    //   renderCell: params => {
    //     return (
    //       <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, height: '100%' }}>
    //         <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>{params?.value}</Typography>
    //         {/* <Divider orientation="vertical" flexItem /> */}
    //       </Box>
    //     )
    //   },
    // },
    {
      field: 'productName',
      headerName: 'Service Name',
      width: 225,
      headerAlign: 'left',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return
        return (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => openInfo(params?.row)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openInfo(params?.row)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              cursor: 'pointer',
              textDecoration: 'none',
              textUnderlineOffset: 2,
              textAlign: 'left',
            }}
          >
            <RenderServiceIcon
              service={params?.row?.productName ?? null}
              size={{
                width: densityStyles.iconSize + 2,
                height: densityStyles.iconSize + 2,
                fontSize: densityStyles.fontSize,
                marginLeft: 2,
              }}
            />
          </Box>
        )
      },
    },
    {
      // Service Line replaces the hidden Category / Subcategory columns. Value
      // is set by backend/scripts/setProductType.ts based on category /
      // subcategory / name conventions (see that script for the rules).
      field: 'product_type',
      headerName: 'Service Line',
      width: 130,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_value, row) => row?.product_type ?? '',
    },
    {
      // Change-type chip — populated by the backend's productsClassification
      // module on /search/products. Compares this product against the matching
      // product on the prior deal version (by productName); New Business is
      // org-wide (no other deal of this org has ever had this product_type).
      field: 'change_type',
      headerName: 'Change',
      width: 140,
      headerAlign: 'left',
      aggregable: false,
      filterOperators: getGridStringOperators(),
      sortComparator: customSortOperator,
      valueGetter: (_value, row) => row?.change_type ?? '',
      renderCell: (params: GridRenderCellParams) => {
        const change = params?.row?.change_type as
          | 'New Business'
          | 'Upsell'
          | 'Downsell'
          | 'Renewal'
          | null
          | undefined
        if (!change) return null
        const priorGp = params?.row?.prior_gross_profit as number | null | undefined
        const currentGp = Number(params?.row?.grossProfit ?? 0)
        const COLOR_BY_CHANGE: Record<string, 'success' | 'primary' | 'warning' | 'default'> = {
          'New Business': 'success',
          Upsell: 'primary',
          Downsell: 'warning',
          Renewal: 'default',
        }
        let tooltip = ''
        if (change === 'New Business') tooltip = 'First time this org has a product of this service line'
        else if (change === 'Renewal' && priorGp != null) tooltip = `Same monthly gross profit as prior version ($${priorGp})`
        else if ((change === 'Upsell' || change === 'Downsell') && priorGp != null) {
          tooltip = `Prior monthly gross profit was $${priorGp} → now $${currentGp}`
        }
        return (
          <Tooltip title={tooltip} arrow>
            <Chip label={change} color={COLOR_BY_CHANGE[change] ?? 'default'} size="small" variant="outlined" />
          </Tooltip>
        )
      },
    },
    { field: '__newStatus', headerName: '→ Status', width: 110 },
    { field: 'deal_end_date', headerName: 'Deal End Date', width: 110 },
    {
      field: 'orgId',
      headerName: 'Org',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const organization = organizations?.find(o => o?.PK === row?.PK)
        return organization?.name ?? null
      },
    },
    {
      field: 'deal_uuid',
      headerName: 'Deal',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const deal = deals?.find(d => d?.deal_uuid === row?.deal_uuid)
        return deal?.name ?? null
      },
      renderCell: params => {
        const deal = deals?.find(d => d?.deal_uuid === params?.row?.deal_uuid)
        const url = `/deals/${deal?.PK}/${deal?.SK}`
        return (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography sx={{ fontSize: 12, color: 'icon.deals' }}>{params?.value}</Typography>
            {/* <Divider orientation="vertical" flexItem /> */}
          </Box>
        )
      },
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const deal = deals?.find(d => d?.deal_uuid === row?.deal_uuid)
        return deal?.dealStatus ?? null
      },
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'deal_type',
      headerName: 'Deal Type',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const deal = deals?.find(d => d?.deal_uuid === row?.deal_uuid)
        return deal?.type ?? null
      },
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'deal_stage',
      headerName: 'Deal Stage',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const deal = deals?.find(d => d?.deal_uuid === row?.deal_uuid)
        return deal?.stage ?? null
      },
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'contracted_by',
      headerName: 'Contracted By',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        return row?.contracted_by?.name ?? null
      },
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1, height: '100%' }}>
            <Typography sx={{ fontSize: 12 }}>{params?.value}</Typography>
          </Box>
        )
      },
    },
    {
      field: 'category_name',
      headerName: 'Category',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
      valueGetter: (_, row) => {
        const cat = row?.category || row?.category_name
        return cat ?? null
      },
    },
    {
      field: 'subcategory',
      headerName: 'Subcategory',
      width: 100,
      headerAlign: 'left',
      aggregable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderStatusCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'dateRange',
      headerName: 'Date Range',
      filterable: true,
      sortable: false,
      aggregable: false,
      width: 0,
      filterOperators: customMonthRangeFilterOperators as any,
      valueGetter: (_, row) => {
        if (!row?.startDate || !row?.endDate) return null
        return [row.startDate, row.endDate]
      },
    },
    {
      field: 'mediaSpend',
      headerName: 'Spend',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.mediaSpend),
      //   valueFormatter: (_, row) => currencyValueFormatter({ value: row?.price }),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.price),
      //   valueFormatter: (_, row) => currencyValueFormatter({ value: row?.price }),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'netTotal',
      headerName: 'Net Total',
      type: 'number',
      width: 100,
      headerAlign: 'left',
      aggregable: true,
      valueGetter: (_, row) => parseNumeric(row?.netTotal),
      //   valueFormatter: (_, row) => currencyValueFormatter({ value: row?.price }),
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
  ]
  return columns
}
